import posthog from "posthog-js";
import { beforeSendToPostHog } from "@/lib/posthog-before-send";
import { CRAWLER_USER_AGENT_TOKENS } from "@/lib/crawler-user-agent";
import { applyClickCapture, isClickEventOutsideMarketing } from "@/lib/posthog-click-capture";
import { sanitizePostHogProperties } from "@/lib/posthog-privacy";
import { clearAnalyticsStorage, getTrackingConsent } from "@/hooks/useTrackingConsent";
import { resumePostHogWithConsent, stopPostHogOnWithdrawal } from "@/lib/posthog-consent";

// PostHog in the browser runs only with analytics consent from the cookie
// banner (#258's store, useTrackingConsent.ts). posthog-js's own opt-out is not
// enough on its own: an opted-out instance still loads its remote config and
// calls /flags with a distinct id. So without consent the SDK is never
// initialised, and calls made meanwhile are dropped, not queued. Server-side
// events (purchases, login telemetry, the photo book) do not pass through here.
// Failure modes: tests/posthog-consent/failure-modes.md.

/**
 * What PostHog does for visitors without analytics consent (rejected, or no
 * choice yet, which includes every guest on an upload page):
 * - "off": nothing is sent (default).
 * - "cookieless": posthog-js's `cookieless_mode: "on_reject"`. Events are sent
 *   without cookies or a stored identity; PostHog counts visitors with a
 *   server-side hash of IP and user agent that rotates daily. It must also be
 *   enabled in the PostHog project settings, or those events are dropped.
 */
export const POSTHOG_WITHOUT_ANALYTICS_CONSENT: "off" | "cookieless" = "off";

const CAPTURE_METHODS = ["capture", "captureException"];
// Identity is never set up without consent, cookieless or not.
const IDENTITY_METHODS = ["identify", "alias"];
const GATED_METHODS = [...CAPTURE_METHODS, ...IDENTITY_METHODS, "reset"];

function isProductionPostHog(): boolean {
  return process.env.NEXT_PUBLIC_VERCEL_ENV === "production" &&
    Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
}

function hasAnalyticsConsent(): boolean {
  return getTrackingConsent().analytics;
}

/** Whether the SDK may run at all for this visitor right now. */
function isPostHogAllowed(): boolean {
  return hasAnalyticsConsent() || POSTHOG_WITHOUT_ANALYTICS_CONSENT === "cookieless";
}

function isCallAllowed(method: string): boolean {
  if (IDENTITY_METHODS.includes(method)) return hasAnalyticsConsent();
  if (CAPTURE_METHODS.includes(method)) return isPostHogAllowed();
  return true;
}

// Preserve early events while initialization yields to the first render.
const pendingEvents: Array<() => void> = [];

function sanitizePersistence(): void {
  // Flags requests bypass before_send and read SDK-cached person/initial URLs.
  // Sanitize those same stores before flags can use them, including old consented state.
  for (const persistence of Array.from(new Set([posthog.persistence, posthog.sessionPersistence]))) {
    if (!persistence) continue;
    const properties = sanitizePostHogProperties(persistence.props);
    if (properties !== persistence.props) persistence.register(properties);
  }
}

// Production-gated, consent-gated, sanitizing client with a pre-load queue.
// Reporting error boundaries fall back to it when they render outside
// CSPostHogProvider. A call without consent is dropped: it is never sent
// later, even if the visitor accepts on the same page.
export const analyticsClient = new Proxy(posthog, {
  get(target, property) {
    if (GATED_METHODS.includes(String(property))) {
      return (...args: unknown[]) => {
        if (!isProductionPostHog() || !isCallAllowed(String(property))) return;
        const deliver = () => {
          // Withdrawn between the call and the SDK loading.
          if (!isCallAllowed(String(property))) return;
          try {
            const safeArgs = args.map((value, index) => index > 0 && value !== null &&
              typeof value === "object" && !(value instanceof Error)
              ? sanitizePostHogProperties(value as Record<string, unknown>) : value);
            return Reflect.apply(Reflect.get(target, property), target, safeArgs);
          } catch {
            // Analytics failures must not interrupt account or media interactions.
          }
        };
        if (target.__loaded) return deliver();
        // Account operations must survive a burst of early diagnostic events.
        const isIdentityOperation = ["identify", "alias", "reset"].includes(String(property));
        if (isIdentityOperation || pendingEvents.length < 100) pendingEvents.push(deliver);
      };
    }
    return Reflect.get(target, property);
  },
});

function initPostHog(bootstrapData: unknown): void {
  const isCookieless = POSTHOG_WITHOUT_ANALYTICS_CONSENT === "cookieless";
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: "/ingest",
    ui_host: "https://eu.i.posthog.com",
    bootstrap: bootstrapData as never,
    // Not opt_out_capturing_by_default: an opted-out start skips loading the
    // stored identity, so consented visitors would get a new one on every
    // page. The SDK only starts with consent (or cookieless), and
    // applyConsentToLoadedPostHog sets PostHog's own opt-in from the banner's
    // choice in loaded(). An opt-out also stops and removes its persistence.
    opt_out_persistence_by_default: true,
    ...(isCookieless ? { cookieless_mode: "on_reject" as const } : {}),
    // Added to posthog-js's own bot list, whose events it drops; checkout
    // refuses the same crawlers (src/lib/crawler-user-agent.ts).
    custom_blocked_useragents: [...CRAWLER_USER_AGENT_TOKENS],
    before_send: (event) => {
      try {
        sanitizePersistence();
      } catch {
        // posthog-js rethrows hook errors from capture(). Cleanup runs again
        // on the next event; this event is still sanitized below.
      }
      if (isClickEventOutsideMarketing(event)) return null;
      return beforeSendToPostHog(event);
    },
    // Rage and dead clicks start off on every page; PostHogClickCapture
    // turns them on for marketing pages only (posthog-click-capture.ts).
    // They use the masks below.
    autocapture: false,
    rageclick: true,
    capture_dead_clicks: false,
    disable_session_recording: true,
    // The site shows no PostHog surveys; this also keeps their script out.
    disable_surveys: true,
    mask_all_element_attributes: true,
    mask_all_text: true,
    persistence: "localStorage+cookie",
    // Enable better error tracking
    capture_pageview: false,
    capture_pageleave: true,
    // Improve debugging
    debug: false,
    // Better error handling
    loaded: (posthog) => {
      // Runs before posthog-js starts its queue and calls /flags.
      applyConsentToLoadedPostHog();
      try {
        sanitizePersistence();
      } catch {
        // posthog-js swallows a loaded() throw and skips the rest of this
        // callback: no metadata, no queued events, no readiness event.
        // before_send retries the cleanup on the next event.
      }
      posthog.register({
        platform: "web",
        deployment_environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
        git_commit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        telemetry_schema_version: 2,
      });
      pendingEvents.splice(0).forEach((deliver) => deliver());
      try {
        applyClickCapture();
      } catch {
        // Click capture is optional; readiness must still be announced.
      }
      window.dispatchEvent(new Event("guestgallery:posthog-loaded"));
    },
  });
}

/**
 * Makes the loaded SDK follow the banner's choice: with consent, opted in
 * (no `$opt_in` event; #258's resumePostHogWithConsent undoes an earlier
 * withdrawal); without it, which only happens in cookieless mode, opted out,
 * so posthog-js sends anonymous cookieless events.
 */
function applyConsentToLoadedPostHog(): void {
  if (!posthog.__loaded) return;
  if (hasAnalyticsConsent()) {
    resumePostHogWithConsent();
    // cookieless_mode "on_reject" reads "no choice" as opted out.
    if (!posthog.has_opted_in_capturing()) posthog.opt_in_capturing({ captureEventName: false });
    return;
  }
  if (posthog.get_explicit_consent_status() !== "denied") posthog.opt_out_capturing();
}

// Set by a withdrawal: the SDK stays stopped until the page reloads, even if
// the visitor accepts again first (re-initialising would flush old queues).
let isStoppedUntilReload = false;

/**
 * The withdrawal handler: #258's opt-out (nothing more is captured, not even
 * the reload's $pageleave), no more /flags, and nothing already queued is
 * sent.
 */
export function stopPostHogNow(): void {
  if (!posthog.__loaded || hasAnalyticsConsent()) return;
  // First: set_config (here and in stopPostHogOnWithdrawal) restarts
  // extensions, and a /flags or config response still in flight can start
  // one later. Their script loader checks this setting, not `__loaded`.
  posthog.set_config({ disable_external_dependency_loading: true });
  stopPostHogOnWithdrawal();
  if (POSTHOG_WITHOUT_ANALYTICS_CONSENT === "off") posthog.set_config({ advanced_disable_flags: true });
  // Opting out stops new captures only: posthog-js (1.294) still sends its
  // batch and retry queues on the reload's unload (sendBeacon), and its retry
  // poller keeps going. Those queues and its send method are private (and
  // minified in the shipped bundle), so there is no supported way to drop
  // them; every send first returns while `__loaded` is false. P22/P23 in
  // tests/posthog-consent run the installed SDK, so an upgrade that renames
  // or stops checking `__loaded` fails them.
  isStoppedUntilReload = true;
  (posthog as { __loaded: boolean }).__loaded = false;
}

/**
 * Starts PostHog once the visitor may be tracked; call on mount and whenever
 * the consent changes. Without consent, PostHog storage left from an earlier
 * consent (expired, removed, or lost while the tab was closed) is cleared, so
 * a stale PostHog opt-in can never start it. Returns a cancel for the idle
 * start.
 */
export function startPostHogWhenAllowed(bootstrapData: unknown): () => void {
  if (typeof window === "undefined" || !isProductionPostHog() || isStoppedUntilReload) return () => {};
  if (posthog.__loaded) {
    applyConsentToLoadedPostHog();
    return () => {};
  }
  if (!hasAnalyticsConsent()) clearAnalyticsStorage();
  if (!isPostHogAllowed()) return () => {};
  const initialize = () => {
    if (!posthog.__loaded && !isStoppedUntilReload && isPostHogAllowed()) initPostHog(bootstrapData);
  };
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(initialize, { timeout: 1000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = setTimeout(initialize, 0);
  return () => clearTimeout(id);
}
